import { useState } from "react";
import {
  useAdminUserStats,
  useAdminRecentUsers,
  useAdminSuspendUser,
  useAdminUnsuspendUser,
  useAdminPromoteUser,
  useAdminDemoteUser,
} from "@/hooks/use-queries";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users,
  UserPlus,
  Bot,
  Briefcase,
  ShieldBan,
  ShieldCheck,
  ShieldOff,
  ShieldPlus,
  ChevronDown,
  ChevronUp,
  Mail,
  MessageSquare,
  Gavel,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { resolveAvatarUrl } from "@/lib/utils";

function StatCard({
  icon: Icon,
  iconColor,
  label,
  value,
  sub,
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card className="glass-panel">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg bg-surface-subtle ${iconColor}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
        <p className="text-2xl font-display font-bold">{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function AdminUsers() {
  const { data: stats, isLoading: statsLoading } = useAdminUserStats();
  const { data: users, isLoading: usersLoading } = useAdminRecentUsers();
  const suspendMutation = useAdminSuspendUser();
  const unsuspendMutation = useAdminUnsuspendUser();
  const promoteMutation = useAdminPromoteUser();
  const demoteMutation = useAdminDemoteUser();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ userId: string; email: string; action: "suspend" | "unsuspend" | "promote" | "demote" } | null>(null);

  const handleAction = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.action === "suspend") {
        await suspendMutation.mutateAsync(confirmAction.userId);
        toast.success(`User ${confirmAction.email} suspended`);
      } else if (confirmAction.action === "unsuspend") {
        await unsuspendMutation.mutateAsync(confirmAction.userId);
        toast.success(`User ${confirmAction.email} unsuspended`);
      } else if (confirmAction.action === "promote") {
        await promoteMutation.mutateAsync(confirmAction.userId);
        toast.success(`${confirmAction.email} promoted to admin`);
      } else {
        await demoteMutation.mutateAsync(confirmAction.userId);
        toast.success(`Admin role removed from ${confirmAction.email}`);
      }
      setConfirmAction(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? `Failed to ${confirmAction.action} user`);
    }
  };

  const isMutating = suspendMutation.isPending || unsuspendMutation.isPending || promoteMutation.isPending || demoteMutation.isPending;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-sm font-mono text-primary uppercase tracking-[0.2em] mb-1">Admin</h2>
        <h1 className="text-3xl font-display font-bold">Users</h1>
        <p className="text-muted-foreground text-sm mt-1">
          User signups, activity, and account management.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {statsLoading ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)
        ) : (
          <>
            <StatCard icon={Users} iconColor="text-blue-400" label="Total Users" value={stats?.total ?? "—"} />
            <StatCard icon={UserPlus} iconColor="text-emerald-400" label="New Today" value={stats?.newToday ?? "—"} />
            <StatCard icon={Bot} iconColor="text-purple-400" label="With Agents" value={stats?.withAgents ?? "—"} />
            <StatCard icon={Briefcase} iconColor="text-amber-400" label="With Jobs" value={stats?.withJobs ?? "—"} />
            <StatCard icon={ShieldBan} iconColor="text-red-400" label="Suspended" value={stats?.suspended ?? 0} />
          </>
        )}
      </div>

      {/* Recent Users Table */}
      <div className="space-y-3">
        <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Users className="h-3.5 w-3.5" /> Recent Users
        </h3>
        <Card className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-divider">
                  <th className="w-8 p-3"></th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">User</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Roles</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Status</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Agents</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Jobs</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Bids</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Msgs</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Source</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Joined</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-divider/60">
                      {Array.from({ length: 11 }).map((_, j) => (
                        <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                      ))}
                    </tr>
                  ))
                ) : !users?.length ? (
                  <tr>
                    <td colSpan={11} className="p-8 text-center text-muted-foreground">
                      <div className="border-2 border-dashed border-divider rounded-lg py-8">
                        No users found.
                      </div>
                    </td>
                  </tr>
                ) : (
                  users.map((user: any) => {
                    const isExpanded = expandedId === user.id;
                    const isSuspended = !!user.suspendedAt;
                    // The API now resolves this: a UTM when tagged, otherwise
                    // the referrer classified into a real channel. "untracked"
                    // was us discarding a referrer we had already stored.
                    const attributionSource =
                      user.acquisition?.source ||
                      user.attribution?.firstUtmSource ||
                      user.attribution?.lastUtmSource ||
                      "direct";
                    const attributionMedium =
                      user.acquisition?.medium ||
                      user.attribution?.firstUtmMedium ||
                      user.attribution?.lastUtmMedium;
                    const attributionCampaign =
                      user.attribution?.firstUtmCampaign ||
                      user.attribution?.lastUtmCampaign;
                    return (
                      <>
                        <tr
                          key={user.id}
                          className={`border-b border-divider/60 hover:bg-surface-subtle transition-colors cursor-pointer ${isSuspended ? "opacity-60" : ""}`}
                          onClick={() => setExpandedId(isExpanded ? null : user.id)}
                        >
                          <td className="p-3 text-center">
                            <button className="text-muted-foreground hover:text-foreground">
                              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2.5">
                              <Avatar className="h-7 w-7">
                                {user.avatarUrl && <AvatarImage src={resolveAvatarUrl(user.avatarUrl)} />}
                                <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                                  {(user.displayName || user.email || "?")[0].toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <p className="truncate max-w-[180px] font-medium">{user.displayName || "—"}</p>
                                <p className="truncate max-w-[180px] text-[10px] text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1 flex-wrap">
                              {(user.roles ?? []).map((role: string) => (
                                <Badge
                                  key={role}
                                  variant="outline"
                                  className={`text-[9px] py-0 ${
                                    role === "ADMIN"
                                      ? "bg-red-500/10 text-red-400 border-red-500/20"
                                      : "bg-surface-subtle text-muted-foreground border-divider"
                                  }`}
                                >
                                  {role}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="p-3">
                            {isSuspended ? (
                              <Badge variant="outline" className="text-[9px] py-0 bg-red-500/10 text-red-400 border-red-500/20">
                                SUSPENDED
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px] py-0 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                                ACTIVE
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">{user._count?.agents ?? 0}</td>
                          <td className="p-3">{user._count?.postedJobs ?? 0}</td>
                          <td className="p-3">{user._count?.bids ?? 0}</td>
                          <td className="p-3">{user._count?.sentMessages ?? 0}</td>
                          <td className="p-3">
                            <div className="min-w-[96px]">
                              <p className="font-medium truncate">{attributionSource}</p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {attributionMedium ?? "direct"}
                              </p>
                            </div>
                          </td>
                          <td className="p-3 text-muted-foreground whitespace-nowrap">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </td>
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              {isSuspended ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[10px] text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                  onClick={() => setConfirmAction({ userId: user.id, email: user.email, action: "unsuspend" })}
                                  disabled={isMutating}
                                >
                                  <ShieldCheck className="h-3 w-3 mr-1" /> Unblock
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={() => setConfirmAction({ userId: user.id, email: user.email, action: "suspend" })}
                                  disabled={isMutating || user.roles?.includes("ADMIN")}
                                >
                                  <ShieldBan className="h-3 w-3 mr-1" /> Block
                                </Button>
                              )}
                              {user.roles?.includes("ADMIN") ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[10px] text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                  onClick={() => setConfirmAction({ userId: user.id, email: user.email, action: "demote" })}
                                  disabled={isMutating}
                                >
                                  <ShieldOff className="h-3 w-3 mr-1" /> Revoke
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[10px] text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                                  onClick={() => setConfirmAction({ userId: user.id, email: user.email, action: "promote" })}
                                  disabled={isMutating}
                                >
                                  <ShieldPlus className="h-3 w-3 mr-1" /> Admin
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Expanded Detail Row */}
                        {isExpanded && (
                          <tr key={`${user.id}-detail`} className="border-b border-divider/60">
                            <td colSpan={11} className="p-0">
                              <div className="px-6 py-4 bg-surface-subtle/60 space-y-4">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">User ID</p>
                                    <p className="text-xs font-mono break-all">{user.id}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Email</p>
                                    <p className="text-xs flex items-center gap-1.5">
                                      <Mail className="h-3 w-3 text-muted-foreground" />
                                      {user.email}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Twitter</p>
                                    <p className="text-xs">
                                      {user.twitterUsername ? (
                                        <a
                                          href={`https://twitter.com/${user.twitterUsername}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-400 hover:underline flex items-center gap-1"
                                        >
                                          @{user.twitterUsername} <ExternalLink className="h-2.5 w-2.5" />
                                        </a>
                                      ) : (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Last Active</p>
                                    <p className="text-xs text-muted-foreground">{new Date(user.updatedAt).toLocaleString()}</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">First Source</p>
                                    <p className="text-xs font-mono">
                                      {attributionSource}
                                      {attributionMedium ? ` / ${attributionMedium}` : ""}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Campaign</p>
                                    <p className="text-xs font-mono break-all">{attributionCampaign || "—"}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Landing</p>
                                    <p className="text-xs font-mono break-all">
                                      {user.attribution?.firstLandingPath || user.attribution?.firstHttpReferrer || "—"}
                                    </p>
                                  </div>
                                </div>

                                {/* Activity summary */}
                                <div className="flex gap-6 text-xs">
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <Bot className="h-3 w-3" /> {user._count?.agents ?? 0} agents
                                  </span>
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <Briefcase className="h-3 w-3" /> {user._count?.postedJobs ?? 0} jobs posted
                                  </span>
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <Gavel className="h-3 w-3" /> {user._count?.bids ?? 0} bids
                                  </span>
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <MessageSquare className="h-3 w-3" /> {user._count?.sentMessages ?? 0} messages
                                  </span>
                                </div>

                                {isSuspended && (
                                  <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-xs text-red-300">
                                    Suspended since {new Date(user.suspendedAt).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Confirm Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.action === "suspend" && "Suspend User?"}
              {confirmAction?.action === "unsuspend" && "Unsuspend User?"}
              {confirmAction?.action === "promote" && "Promote to Admin?"}
              {confirmAction?.action === "demote" && "Revoke Admin Role?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.action === "suspend" &&
                `This will block ${confirmAction.email} from the platform. They will not be able to log in or take any actions.`}
              {confirmAction?.action === "unsuspend" &&
                `This will restore access for ${confirmAction.email}. They will be able to log in and use the platform again.`}
              {confirmAction?.action === "promote" &&
                `This will give ${confirmAction.email} full admin access to this dashboard, including user management and platform configuration.`}
              {confirmAction?.action === "demote" &&
                `This will remove admin privileges from ${confirmAction.email}. They will revert to a regular user account.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={
                confirmAction?.action === "suspend"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : confirmAction?.action === "promote"
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : confirmAction?.action === "demote"
                  ? "bg-amber-600 text-white hover:bg-amber-700"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }
            >
              {isMutating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : confirmAction?.action === "suspend" ? (
                "Suspend User"
              ) : confirmAction?.action === "unsuspend" ? (
                "Unsuspend User"
              ) : confirmAction?.action === "promote" ? (
                "Make Admin"
              ) : (
                "Revoke Admin"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
